import React, { useContext, useRef, useMemo } from 'react';
import { Row } from 'react-table';
import '../common/datatables/ReactTableStyle.css';
import { Row as TRow, Col, ProgressBar, Button, Alert } from 'react-bootstrap';
import { Link, RouteComponentProps } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { RoleUtils, HasRoles } from '@galaxy/lorule-react-commons';
import moment from 'moment-timezone';
import _ from 'lodash';
import { renderOverlay, getTruncatedSubject } from '../common/listUtils';
import ConfigContext from '../../contexts/ConfigContext';
import { AmStatusType, ApproverRoutingStatusType, AM_LIST_PAGE_NAME } from '../../utils/Constants';
import { getActionMemo } from '../../api/actionMemoApi';
import useAsyncWithState from '../../hooks/useAsyncWithState';
import { ActionMemo } from '../../api/actionMemoApiTypes';
import AmCreateModal from '../common/modal/AmCreateModal';
import { AmCreateFormType } from '../common/modal/amCreateformTypes';
import {
    formatCurrency,
    formatNameTitle,
    formatAmDisplayString,
    convertEmptyStringToDashes,
    getStatusBadge,
    formatDate,
    getDueInBadgeStyle,
} from '../../utils/displayUtils';
import Spinner from '../common/Spinner';
import FavoriteButton from '../common/FavoriteButton';
import { ActionMemoItem } from '../../api/actionMemoListApiTypes';
import AddViewerModal from '../common/modal/AddViewerModal';

export interface RowType {
    original: ActionMemoItem;
}

export const generateActionMemoColumns = (
    routerProps: RouteComponentProps,
    returnPageName: string,
    returnPathName: string,
    setResponseError: Function,
    refresh?: Function,
    selecting?: boolean,
    selectedRecords?: Map<number, ActionMemoItem>,
    setSelectedRecords?: (selectedRecords: Map<number, ActionMemoItem>) => void,
) => {
    const refreshRef = useRef(0);
    const refreshList = () => {
        if (refresh) {
            refreshRef.current++;
            refresh(refreshRef.current);
        }
    };

    const startOfDay = moment().startOf('day');

    return React.useMemo(
        () => [
            {
                id: 'expander',
                accessor: '', // accessor is the "key" in the data
                Header: (table: { page: RowType[] }) =>
                    selecting && selectedRecords && setSelectedRecords ? (
                        <span>
                            <i className="fas fa-plus-square invisible" />
                            <input
                                className="ml-3"
                                type="checkbox"
                                checked={table.page.every((row) => {
                                    if (
                                        row.original.amStatus !== AmStatusType.Pending &&
                                        row.original.amStatus !== AmStatusType.Hold &&
                                        row.original.amStatus !== AmStatusType.Approved
                                    ) {
                                        return true;
                                    }
                                    return selectedRecords.has(row.original.id);
                                })}
                                onChange={(e) => {
                                    const selectedRecordsMap = _.cloneDeep(selectedRecords);
                                    table.page.forEach((row) => {
                                        if (e.target.checked) {
                                            if (
                                                row.original.amStatus === AmStatusType.Pending ||
                                                row.original.amStatus === AmStatusType.Hold ||
                                                row.original.amStatus === AmStatusType.Approved
                                            ) {
                                                selectedRecordsMap.set(
                                                    row.original.id,
                                                    row.original,
                                                );
                                            }
                                        } else {
                                            selectedRecordsMap.delete(row.original.id);
                                        }
                                    });
                                    setSelectedRecords(selectedRecordsMap);
                                }}
                            />
                        </span>
                    ) : null,
                Cell: ({ row }: { row: Row<ActionMemoItem> }) => (
                    <span {...row.getToggleRowExpandedProps()}>
                        {row.isExpanded ? (
                            <i className="fas fa-minus-square" />
                        ) : (
                            <i className="fas fa-plus-square" />
                        )}
                        {selecting &&
                        selectedRecords &&
                        setSelectedRecords &&
                        row.original.id &&
                        (row.original.amStatus === AmStatusType.Pending ||
                            row.original.amStatus === AmStatusType.Hold ||
                            row.original.amStatus === AmStatusType.Approved) ? (
                            <input
                                key={`${row.original.id}`}
                                className="ml-3"
                                type="checkbox"
                                checked={selectedRecords.has(row.original.id)}
                                onChange={(e) => {
                                    if (row.original.id) {
                                        const selectedRecordsMap = _.cloneDeep(selectedRecords);
                                        if (e.target.checked) {
                                            selectedRecordsMap.set(row.original.id, row.original);
                                        } else {
                                            selectedRecordsMap.delete(row.original.id);
                                        }

                                        setSelectedRecords(selectedRecordsMap);
                                    }
                                }}
                            />
                        ) : (
                            <input type="checkbox" className="invisible ml-3" />
                        )}
                    </span>
                ),
                disableSortBy: true,
                sm: 1,
                xl: 1,
                xs: 1,
                customClass: 'am-list-action align-middle',
            },
            {
                Header: 'AM#',
                accessor: 'amNumber',
                Cell: (cellProps: { value: number; row: RowType }) =>
                    useMemo(() => {
                        const favoriteButton = (watching: boolean, onClick: () => void) => (
                            <button
                                type="button"
                                className="am-list-favorite dropdown-menu-modal-toggle"
                                onClick={onClick}
                            >
                                {watching ? (
                                    <i className="fas fa-heart mr-3 text-danger" />
                                ) : (
                                    <i className="fal fa-heart mr-3" />
                                )}
                            </button>
                        );
                        return (
                            <>
                                <Link
                                    to={{
                                        pathname: `/details/${cellProps.row.original.id}`,
                                        state: { returnPageName, returnPathName },
                                    }}
                                >
                                    {formatAmDisplayString(
                                        cellProps.row.original.amNumber,
                                        cellProps.row.original.supNumber,
                                    )}
                                </Link>
                                <FavoriteButton
                                    initialFav={cellProps.row.original.watching}
                                    id={cellProps.row.original.id}
                                    amNumber={cellProps.row.original.amNumber}
                                    supNumber={cellProps.row.original.supNumber}
                                    setResponseError={setResponseError}
                                    button={favoriteButton}
                                    refresh={refreshList}
                                />
                            </>
                        );
                    }, []),
                xs: 12,
                sm: 12,
                xl: 1,
                customClass: 'text-nowrap',
            },
            {
                Header: 'Subject',
                accessor: 'subject',
                Cell: (cellProps: { value: string }) => getTruncatedSubject(cellProps.value),
            },
            {
                Header: 'Budget',
                accessor: 'budgetAmount',
                Cell: (cellProps: { value: number }) => (
                    <span className="desktop-float-right pr-3">
                        {formatCurrency(cellProps.value, 2)}
                    </span>
                ),
                xs: 12,
                sm: 12,
                xl: 1,
                sortType: 'basic',
            },
            {
                Header: 'Due In',
                accessor: 'dueDate',
                Cell: (cellProps: { value: string }) => {
                    if (!cellProps.value) {
                        return '--';
                    }
                    const dueDateMoment = moment(cellProps.value);
                    const dueIn = dueDateMoment.diff(startOfDay, 'days');
                    let displayString = '';
                    let mobileDisplayString = '';
                    if (dueIn < 0) {
                        displayString = 'Overdue';
                    } else if (dueIn < 1) {
                        displayString = 'Today';
                        mobileDisplayString = `Due ${displayString}`;
                    } else {
                        displayString = `${dueIn} day${dueIn > 1 ? 's' : ''}`;
                        mobileDisplayString = `Due in ${displayString}`;
                    }
                    return (
                        <>
                            <div className="desktop-hide">
                                <span className={`badge ${getDueInBadgeStyle(dueIn)}`}>
                                    {mobileDisplayString || displayString}
                                </span>
                            </div>
                            <span className="mobile-hide">{displayString}</span>
                        </>
                    );
                },
                xs: 7,
                sm: 7,
                xl: 1,
            },

            {
                Header: 'Status',
                accessor: 'amStatus',
                Cell: (cellProps: { value: string }) => getStatusBadge(cellProps.value, true),
                xs: 4,
                sm: 4,
                xl: 1,
                customClass: 'am-list-status',
            },
            {
                Header: 'Current Approver',
                accessor: 'currentApproverName',
                Cell: (cellProps: { value: string }) => convertEmptyStringToDashes(cellProps.value),
                xs: 6,
                sm: 6,
                xl: 2,
            },
            {
                Header: 'Progress',
                accessor: 'maxStopNumber',
                disableSortBy: true,
                Cell: (cellProps: { value: number; row: RowType }) =>
                    cellProps.value && cellProps.row.original.currentStopNumber ? (
                        <>
                            <div className="progress">
                                <ProgressBar
                                    variant="danger"
                                    now={
                                        (cellProps.row.original.currentStopNumber /
                                            cellProps.value) *
                                        100
                                    }
                                />
                            </div>

                            <div className="progress-value">
                                {cellProps.row.original.currentStopNumber} / {cellProps.value}
                            </div>
                        </>
                    ) : (
                        convertEmptyStringToDashes('')
                    ),
                xs: 12,
                sm: 12,
                xl: 2,
                customClass: 'am-list-progress',
            },

            {
                Header: '',
                accessor: 'actionMemoRowMenu',
                Cell: (cellProps: { row: RowType }) =>
                    useMemo(() => {
                        const favoriteButton = (watching: boolean, onClick: () => void) => (
                            <button
                                type="button"
                                className="dropdown-menu-modal-toggle"
                                onClick={onClick}
                            >
                                {watching ? (
                                    <>
                                        <i className="fas fa-heart text-danger button-icon" />{' '}
                                        Remove from Favorites
                                    </>
                                ) : (
                                    <>
                                        <i className="fal fa-heart button-icon" /> Add to Favorites
                                    </>
                                )}
                            </button>
                        );
                        return (
                            <>
                                <Button
                                    variant="link"
                                    className="btn-link p-0"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    <i className="fas fa-ellipsis-h text-muted" />
                                </Button>

                                <ul className="dropdown-menu dropdown-menu-right">
                                    <li>
                                        <button
                                            type="button"
                                            className="dropdown-menu-modal-toggle"
                                            onClick={() => {
                                                routerProps.history.push({
                                                    pathname: `/details/${cellProps.row.original.id}`,
                                                    state: { returnPageName, returnPathName },
                                                });
                                            }}
                                        >
                                            <i className="far fa-info-circle button-icon" /> AM
                                            Details
                                        </button>
                                    </li>
                                    {cellProps.row.original.supNumber === 0 &&
                                        cellProps.row.original.amStatus ===
                                            AmStatusType.Approved && (
                                            <li>
                                                <AmCreateModal
                                                    title="Add Supplement"
                                                    toggleButton={
                                                        <button
                                                            type="button"
                                                            className="dropdown-menu-modal-toggle"
                                                        >
                                                            <i className="far fa-plus button-icon" />{' '}
                                                            Add Supplement
                                                        </button>
                                                    }
                                                    formType={AmCreateFormType.SUPPLEMENT}
                                                    amId={cellProps.row.original.id}
                                                    projectManagerId={
                                                        cellProps.row.original.projectManagerId
                                                    }
                                                    projectManagerName={
                                                        cellProps.row.original.projectManagerName
                                                    }
                                                    onSubmit={() => {}}
                                                    routerProps={routerProps}
                                                    subject={cellProps.row.original.subject}
                                                />
                                            </li>
                                        )}
                                    {(cellProps.row.original.amStatus === AmStatusType.Draft ||
                                        cellProps.row.original.amStatus === AmStatusType.Pending ||
                                        cellProps.row.original.amStatus === AmStatusType.Hold ||
                                        cellProps.row.original.amStatus ===
                                            AmStatusType.Approved) && (
                                        <li>
                                            <AmCreateModal
                                                title="Copy Action Memo "
                                                toggleButton={
                                                    <button
                                                        type="button"
                                                        className="dropdown-menu-modal-toggle"
                                                    >
                                                        <i className="far fa-clone button-icon" />{' '}
                                                        Copy AM
                                                    </button>
                                                }
                                                formType={AmCreateFormType.COPY}
                                                onSubmit={() => {}}
                                                amId={cellProps.row.original.id}
                                                orgId={cellProps.row.original.orgId}
                                                subject={`COPY OF ${cellProps.row.original.subject}`}
                                                projectManagerId={
                                                    cellProps.row.original.projectManagerId
                                                }
                                                projectManagerName={
                                                    cellProps.row.original.projectManagerName
                                                }
                                                routerProps={routerProps}
                                            />
                                        </li>
                                    )}
                                    {(cellProps.row.original.amStatus === AmStatusType.Pending ||
                                        cellProps.row.original.amStatus === AmStatusType.Hold ||
                                        cellProps.row.original.amStatus ===
                                            AmStatusType.Approved) &&
                                        returnPageName === AM_LIST_PAGE_NAME && (
                                            <HasRoles roles={['ActionMemo.Admin']}>
                                                <li>
                                                    <AddViewerModal
                                                        title="Add Viewer"
                                                        toggleButton={
                                                            <button
                                                                type="button"
                                                                className="dropdown-menu-modal-toggle"
                                                            >
                                                                <i className="far fa-user-plus button-icon" />{' '}
                                                                Add Viewer
                                                            </button>
                                                        }
                                                        bulkSelectItems={[
                                                            {
                                                                amId: cellProps.row.original.id,
                                                                amNumber:
                                                                    cellProps.row.original.amNumber,
                                                                supNumber:
                                                                    cellProps.row.original
                                                                        .supNumber,
                                                            },
                                                        ]}
                                                        onSubmit={refreshList}
                                                    />
                                                </li>
                                            </HasRoles>
                                        )}
                                    <li>
                                        <FavoriteButton
                                            initialFav={cellProps.row.original.watching}
                                            id={cellProps.row.original.id}
                                            amNumber={cellProps.row.original.amNumber}
                                            supNumber={cellProps.row.original.supNumber}
                                            setResponseError={setResponseError}
                                            button={favoriteButton}
                                            refresh={refreshList}
                                        />
                                    </li>
                                </ul>
                            </>
                        );
                    }, []),
                disableSortBy: true,
                xs: 1,
                sm: 1,
                xl: 1,
                customClass: 'am-list-action',
            },
        ],
        [refreshRef.current, selecting, selectedRecords],
    );
};

const SubRowAsync = ({ row }: { row: RowType }) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const { value, loading, error } = useAsyncWithState(
        () =>
            getActionMemo(
                config,
                row.original.id,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            ),
        {} as ActionMemo,
    );

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    const contractSigner =
        value.approvers && value.approvers.find((approver) => approver.origin === 'SIGNER');
    const currentApprover =
        (value.amStatus === AmStatusType.Pending || value.amStatus === AmStatusType.Hold) &&
        value.approvers.find(
            (approver) => approver.routingStatus === ApproverRoutingStatusType.CURRENT,
        );

    return loading ? (
        <Spinner />
    ) : (
        <div className="row am-list-properties mobile-hide">
            <div className="col">
                <TRow>
                    <Col sm="5">Creator </Col>
                    <Col sm="7" className="border-bottom p-0">
                        {value.amCreatorName}
                    </Col>
                </TRow>
                <TRow>
                    <Col sm="5">Project Manager </Col>
                    <Col sm="7" className="border-bottom p-0">
                        {value.projectManagerName}
                    </Col>
                </TRow>
                <TRow>
                    <Col sm="5">Created On</Col>
                    <Col sm="7" className="border-bottom p-0">
                        {formatDate(value.creationDate)}
                    </Col>
                </TRow>
                {value.approvalDate && (
                    <TRow>
                        <Col sm="5">Approved On</Col>
                        <Col sm="7" className="border-bottom p-0">
                            {formatDate(value.approvalDate)}
                        </Col>
                    </TRow>
                )}
                {value.dueDate && (
                    <TRow>
                        <Col sm="5">
                            Due On{' '}
                            {renderOverlay(
                                'The date your AM approval will expire and escalate to the next approver',
                            )}
                        </Col>
                        <Col sm="7" className="border-bottom p-0">
                            {value.dueDate && formatDate(value.dueDate)}
                        </Col>
                    </TRow>
                )}
                <TRow>
                    <Col sm="5">Completion Date</Col>
                    <Col sm="7" className="border-bottom p-0">
                        {formatDate(value.completionDate)}
                    </Col>
                </TRow>
            </div>

            <div className="col">
                <TRow>
                    <Col sm="4">Organization</Col>
                    <Col sm="8" className="border-bottom p-0">
                        {value.organizationName}
                    </Col>
                </TRow>
                <TRow>
                    <Col sm="4">Template Code</Col>
                    <Col sm="8" className="border-bottom p-0">
                        {value.templateCode}
                    </Col>
                </TRow>
                <TRow>
                    <Col sm="4">Description</Col>
                    <Col sm="8" className="border-bottom p-0">
                        {value.templateDescription}
                    </Col>
                </TRow>
                {}
            </div>
            <div className="col">
                <TRow>
                    <Col sm="4">Confidential </Col>
                    <Col sm="8" className="border-bottom p-0">
                        {value.confidential ? (
                            <span
                                className={`badge ${
                                    value.confidential
                                        ? ' text-dark badge-warning '
                                        : ' text-white badge-success'
                                }`}
                            >
                                YES
                            </span>
                        ) : (
                            'No'
                        )}
                    </Col>
                </TRow>
                {currentApprover && (
                    <TRow>
                        <Col sm="4">Current Approver</Col>
                        <Col sm="8" className="border-bottom p-0">
                            {formatNameTitle(
                                currentApprover.approverName,
                                currentApprover.approverTitle,
                            )}
                        </Col>
                    </TRow>
                )}
                {contractSigner && (
                    <>
                        {value.contractOrganizationName && (
                            <TRow>
                                <Col sm="4">Contract Entity</Col>
                                <Col sm="8" className="border-bottom p-0">
                                    {value.contractOrganizationName}
                                </Col>
                            </TRow>
                        )}
                        <TRow>
                            <Col sm="4">Contract Signer</Col>
                            <Col sm="8" className="border-bottom p-0">
                                {formatNameTitle(
                                    contractSigner.approverName,
                                    contractSigner.approverTitle,
                                )}
                            </Col>
                        </TRow>
                    </>
                )}
            </div>
        </div>
    );
};

export const generateSubRowRenderFunction = () =>
    React.useCallback(({ row }: { row: RowType }) => <SubRowAsync row={row} />, []);
